import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useMemo } from 'react';
import { useDeleteWhatsappIntegration } from '../../../api/delete-integration-whatsapp.api';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY } from '../../../api/get-list-integration-whatsapp.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';

export type TDeleteIntegrationWhatsappModalType = 'DeleteIntegrationWhatsappModal';
export interface IDeleteIntegrationWhatsappModalProps {
  organizationChannelId: string;
}

const TOAST_MESSAGES_DELETE_INTEGRATION_WHATSAPP = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete whatsapp integration!',
};

const DeleteIntegrationWhatsappModal = memo(({ organizationChannelId }: IDeleteIntegrationWhatsappModalProps) => {
  const showToast = useCustomToast();
  const channelModalRootActions = useChannelModalRootActionsStore();

  const isOpenDeleteIntegrationWhatsappModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('DeleteIntegrationWhatsappModal');
  }, [channelModalRootActions]);

  const mutationDeleteIntegrationWhatsapp = useDeleteWhatsappIntegration({
    mutationConfig: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || TOAST_MESSAGES_DELETE_INTEGRATION_WHATSAPP.error;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY] })]);
        channelModalRootActions.closeModal('DeleteIntegrationWhatsappModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_INTEGRATION_WHATSAPP.success });
      },
    },
  });

  const handleDelete = useCallback(() => {
    if (organizationChannelId) {
      mutationDeleteIntegrationWhatsapp.mutate(organizationChannelId);
    }
  }, [organizationChannelId, mutationDeleteIntegrationWhatsapp]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('DeleteIntegrationWhatsappModal');
  }, [channelModalRootActions]);

  return (
    <ModalWithConfirmation
      isOpen={isOpenDeleteIntegrationWhatsappModal}
      onClose={handleClose}
      onDelete={handleDelete}
      isLoading={mutationDeleteIntegrationWhatsapp.isPending}
      title="Delete Channel"
      description="Do you want to delete this channel?"
      bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
      confirmText="delete my channel"
    />
  );
});

DeleteIntegrationWhatsappModal.displayName = 'DeleteIntegrationWhatsappModal';

export default DeleteIntegrationWhatsappModal;
